import axios from "axios";
import logger from "../utils/logger";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Log request details
api.interceptors.request.use(
  (config) => {
    logger.log(
      `📤 API Request: ${config.method.toUpperCase()} ${config.baseURL}${
        config.url
      }`,
      config.params ? `\nParams: ${JSON.stringify(config.params)}` : "",
      config.data ? `\nData: ${JSON.stringify(config.data)}` : ""
    );
    return config;
  },
  (error) => {
    logger.error("📤 API Request Error:", error);
    return Promise.reject(error);
  }
);

// Log response details
api.interceptors.response.use(
  (response) => {
    logger.log(
      `📥 API Response: ${response.status} from ${response.config.url}`
    );
    return response;
  },
  (error) => {
    logger.error(
      "📥 API Response Error:",
      error.response?.status || "Unknown",
      error.response?.data || error.message,
      `\nURL: ${error.config?.url}`
    );
    
    // Handle 401 Unauthorized — token expired or invalid
    if (error.response?.status === 401) {
      const url = error.config?.url || "";
      const method = (error.config?.method || "get").toLowerCase();
      // Background GETs for preferences: clear credentials without broadcasting
      // session-end (avoids wiping UI auth state on transient / race failures while browsing).
      const silentPreferenceGet =
        method === "get" &&
        (url.includes("/users/saved-deals") ||
          url.includes("/users/followed-brands") ||
          url.includes("/users/filter-preferences") ||
          url.includes("/users/favorites"));

      localStorage.removeItem("token");
      delete api.defaults.headers.common["Authorization"];

      if (silentPreferenceGet) {
        return Promise.reject(error);
      }

      const isProtectedRoute =
        url.includes("/users/") ||
        url.includes("/orders") ||
        url.includes("/auth/profile");

      if (isProtectedRoute) {
        window.dispatchEvent(
          new CustomEvent("auth:logout", {
            detail: { reason: "token_expired" },
          }),
        );
      } else {
        window.dispatchEvent(new CustomEvent("auth:token_cleared"));
      }
    }
    
    return Promise.reject(error);
  }
);

// Add token to requests if it exists
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth APIs
export const authAPI = {
  // Email authentication methods
  register: (userData) => api.post("/auth/register", userData),
  login: (email, password) => api.post("/auth/login", { email, password }),
  googleAuth: (tokenId) => api.post("/auth/google", { tokenId }),
  getProfile: () => api.get("/auth/profile"),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (token, newPassword) =>
    api.post("/auth/reset-password", { token, password: newPassword }),

  // Legacy phone authentication methods (can be removed when fully migrated)
  sendOTP: (phoneNumber) => api.post("/auth/send-otp", { phone: phoneNumber }),
  verifyOTP: (phoneNumber, otp) =>
    api.post("/auth/verify-otp", { phone: phoneNumber, otp }),
};

// Restaurant APIs
export const restaurantAPI = {
  getRestaurant: (id) => api.get(`/restaurants/${id}`),
  updateRestaurant: (id, data) => api.put(`/restaurants/${id}`, data),
  getMenuItems: (restaurantId) => api.get(`/menus/${restaurantId}`),
  createMenuItem: (restaurantId, data) =>
    api.post(`/menus/${restaurantId}/items`, data),
  updateMenuItem: (restaurantId, itemId, data) =>
    api.put(`/menus/${restaurantId}/items/${itemId}`, data),
  deleteMenuItem: (restaurantId, itemId) =>
    api.delete(`/menus/${restaurantId}/items/${itemId}`),
};

// Voucher APIs
export const voucherAPI = {
  getVouchers: (restaurantId) => {
    logger.log("Making request to:", `/vouchers/restaurant/${restaurantId}`);
    return api.get(`/vouchers/restaurant/${restaurantId}`);
  },
  getFeaturedVouchers: (limit = 4) => {
    logger.log("Making request to:", `/vouchers/featured?limit=${limit}`);
    return api.get(`/vouchers/featured?limit=${limit}`);
  },

  getVoucherById: (voucherId) => {
    logger.log("Making request to:", `/vouchers/${voucherId}`);
    return api.get(`/vouchers/${voucherId}`);
  },
  createVoucher: (data) => api.post(`/vouchers`, data),
  updateVoucher: (voucherId, data) => api.put(`/vouchers/${voucherId}`, data),
  deleteVoucher: (voucherId) => api.delete(`/vouchers/${voucherId}`),
  reserveVoucher: (voucherId) => api.post(`/vouchers/${voucherId}/reserve`),
  getUserVouchers: () => api.get("/vouchers/user/my"),
};

// Order APIs
export const orderAPI = {
  getOrders: (restaurantId) => api.get(`/restaurants/${restaurantId}/orders`),
  getOrder: (restaurantId, orderId) =>
    api.get(`/restaurants/${restaurantId}/orders/${orderId}`),
  updateOrderStatus: (restaurantId, orderId, status) =>
    api.put(`/restaurants/${restaurantId}/orders/${orderId}/status`, {
      status,
    }),
  getUserOrders: () => api.get("/orders/my"),
  createOrder: (orderData) => api.post("/orders", orderData),
};

// Deal APIs
export const dealAPI = {
  searchDeals: (query, limit = 10) =>
    api.get("/deals/search", { params: { query, limit } }),
  getLocations: () => api.get("/deals/locations"),
};

// User APIs
export const userAPI = {
  getProfile: () => api.get("/users/profile"),
  updateProfile: (data) => api.put("/users/profile", data),
  getSavedDeals: () => api.get("/users/saved-deals"),
  saveDeal: (dealId) => api.post("/users/saved-deals", { dealId }),
  unsaveDeal: (dealId) => api.delete(`/users/saved-deals/${dealId}`),
  getFollowedBrands: () => api.get("/users/followed-brands"),
  followBrand: (brandId) => api.post("/users/followed-brands", { brandId }),
  unfollowBrand: (brandId) => api.delete(`/users/followed-brands/${brandId}`),
  getFavorites: () => api.get("/users/favorites"),
  addFavorite: (cuisine) => api.post("/users/favorites", { cuisine }),
  removeFavorite: (cuisine) => api.delete(`/users/favorites/${cuisine}`),
  getUserOrders: () => api.get("/users/orders"),
  getFilterPreferences: () => api.get("/users/filter-preferences"),
  updateFilterPreferences: (filters) =>
    api.put("/users/filter-preferences", filters),
};

export default api;
